/**
 * Regulatory reports (manager / super_admin): the investor register and the private-offering
 * defense report for one vehicle, and the two securities-filing feeders for counsel (Form D
 * clock, state notice clock). The platform files nothing; these are the facts counsel files from.
 *
 * Every report lists people or ties a person to a residence, so every view and every download
 * is recorded in the audit log under the reader's name before the data is sent.
 */
import { useEffect, useState, type ReactNode } from 'react';
import PageHeader from '../../components/layout/PageHeader';
import portfolioService, { type ProductSummary } from '../../services/portfolio.service';
import reportsService, {
  type DefenseRow,
  type FirstSales,
  type InvestorRegister,
  type OfferingDefense,
  type RegisterRow,
  type ReportFile,
  type StateNotices,
} from '../../services/regulatoryReports.service';
import { getApiErrorMessage } from '../../lib/apiError';
import { formatDateTime } from '../../lib/datetime';
import { colors } from '../../styles/theme';
import {
  accreditationLabel,
  filingReportBlockedReason,
  moneyMapText,
  relationshipEvidenceLabel,
  residenceBasisLabel,
  residenceText,
  stateLabel,
  vehicleNameFor,
  vehicleReportBlockedReason,
  yesNo,
} from './regulatoryReports.helpers';

function saveFile(file: ReportFile): void {
  const url = URL.createObjectURL(file.blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = file.filename;
  a.click();
  URL.revokeObjectURL(url);
}

// ── Load state ──────────────────────────────────────────────────────────────

export type LoadState<T> =
  | { status: 'idle' }
  | { status: 'loading' }
  | { status: 'error'; message: string }
  | { status: 'ready'; data: T };

interface Report<T> {
  state: LoadState<T>;
  busy: boolean;
  notice: string | null;
  show: (load: () => Promise<T>, failure: string) => Promise<void>;
  download: (load: () => Promise<ReportFile>, failure: string) => Promise<void>;
}

function useReport<T>(): Report<T> {
  const [state, setState] = useState<LoadState<T>>({ status: 'idle' });
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);

  const show = async (load: () => Promise<T>, failure: string): Promise<void> => {
    setBusy(true);
    setNotice(null);
    setState({ status: 'loading' });
    try {
      setState({ status: 'ready', data: await load() });
    } catch (e: unknown) {
      setState({ status: 'error', message: getApiErrorMessage(e, failure) });
    } finally {
      setBusy(false);
    }
  };

  const download = async (load: () => Promise<ReportFile>, failure: string): Promise<void> => {
    setBusy(true);
    setNotice(null);
    setState({ status: 'idle' });
    try {
      const file = await load();
      saveFile(file);
      setNotice(`Downloaded ${file.filename}. This download is recorded in the audit log.`);
    } catch (e: unknown) {
      setState({ status: 'error', message: getApiErrorMessage(e, failure) });
    } finally {
      setBusy(false);
    }
  };

  return { state, busy, notice, show, download };
}

// ── Shared presentation ─────────────────────────────────────────────────────

const field = { border: `1px solid ${colors.border}` };
const labelCls = 'block text-xs font-semibold mb-1';
const thCls = 'text-left text-xs font-semibold px-3 py-2 whitespace-nowrap';
const tdCls = 'px-3 py-2 align-top text-sm';

export function ReportCard(props: Readonly<{
  title: string;
  description: string;
  children: ReactNode;
}>): JSX.Element {
  return (
    <section className="rounded-2xl p-5 mb-6"
             style={{ background: colors.surface, border: `1px solid ${colors.border}` }}>
      <h2 className="text-base font-bold mb-1" style={{ color: colors.ink }}>{props.title}</h2>
      <p className="text-sm mb-4" style={{ color: colors.inkSoft }}>{props.description}</p>
      {props.children}
    </section>
  );
}

/** The two ways to take a report, and why they are off when they are. */
export function ReportActions(props: Readonly<{
  blocked: string | null;
  busy: boolean;
  showLabel: string;
  onShow: () => void;
  onDownload: () => void;
}>): JSX.Element {
  const off = props.blocked !== null;
  return (
    <div className="flex items-center gap-3 flex-wrap mb-3">
      <button type="button" disabled={off} onClick={props.onShow}
              className="px-4 py-2 rounded-lg text-sm font-semibold disabled:opacity-50"
              style={{ background: colors.navy, color: '#FFFFFF' }}>
        {props.busy ? 'Working…' : props.showLabel}
      </button>
      <button type="button" disabled={off} onClick={props.onDownload}
              className="px-4 py-2 rounded-lg text-sm font-semibold disabled:opacity-50"
              style={{ border: `1px solid ${colors.border}`, color: colors.ink }}>
        Download CSV
      </button>
      {props.blocked && (
        <span className="text-xs" style={{ color: colors.muted }}>{props.blocked}</span>
      )}
    </div>
  );
}

/** Idle, loading, could-not-be-read and the download confirmation; the data itself is `children`. */
export function ReportStatus<T>(props: Readonly<{
  state: LoadState<T>;
  notice: string | null;
  children: (data: T) => ReactNode;
}>): JSX.Element {
  const { state, notice } = props;
  return (
    <div>
      {notice && (
        <output className="block text-sm mb-3" style={{ color: colors.success }}>{notice}</output>
      )}
      {state.status === 'loading' && (
        <output className="block text-sm" style={{ color: colors.muted }}>Loading the report…</output>
      )}
      {state.status === 'error' && (
        <p role="alert" className="text-sm" style={{ color: colors.danger }}>
          This report could not be read: {state.message}
        </p>
      )}
      {state.status === 'ready' && props.children(state.data)}
    </div>
  );
}

function Facts(props: Readonly<{ facts: Array<[string, string]> }>): JSX.Element {
  return (
    <dl className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-2 text-sm mb-4">
      {props.facts.map(([term, value]) => (
        <div key={term}>
          <dt className="text-xs font-semibold" style={{ color: colors.muted }}>{term}</dt>
          <dd style={{ color: colors.ink }}>{value}</dd>
        </div>
      ))}
    </dl>
  );
}

function EmptyRows(props: Readonly<{ text: string }>): JSX.Element {
  return <p className="text-sm" style={{ color: colors.muted }}>{props.text}</p>;
}

function Table(props: Readonly<{ caption: string; heads: string[]; children: ReactNode }>): JSX.Element {
  return (
    <div className="overflow-x-auto">
      <table className="w-full" style={{ borderCollapse: 'collapse' }}>
        <caption className="sr-only">{props.caption}</caption>
        <thead>
          <tr style={{ borderBottom: `1px solid ${colors.border}`, color: colors.inkSoft }}>
            {props.heads.map((h) => (
              <th key={h} scope="col" className={thCls}>{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>{props.children}</tbody>
      </table>
    </div>
  );
}

const rowStyle = { borderBottom: `1px solid ${colors.borderSoft}`, color: colors.ink };

// ── The four report views (pure: data in, markup out) ───────────────────────

export function InvestorRegisterView(props: Readonly<{ data: InvestorRegister }>): JSX.Element {
  const { data } = props;
  return (
    <div>
      <Facts facts={[
        ['Vehicle', data.vehicle_name],
        ['As of', data.as_of],
        ['Investors', String(data.investors_total)],
        ['Gross contributed', moneyMapText(data.gross_by_currency)],
        ['Markets', data.supported_markets.join(', ') || '—'],
      ]} />
      {data.items.length === 0 ? (
        <EmptyRows text="No investor has an approved deposit in this vehicle up to the as-of day." />
      ) : (
        <Table caption="Investor register"
               heads={['Investor', 'Residence', 'Accreditation', 'Contributions',
                       'Gross contributed', 'Share of vehicle (fraction of 1)']}>
          {data.items.map((r: RegisterRow) => (
            <tr key={r.user_id} style={rowStyle}>
              <td className={tdCls}>
                <div className="font-semibold">{r.full_name}</div>
                <div className="text-xs" style={{ color: colors.muted }}>{r.email}</div>
              </td>
              <td className={tdCls}>
                <div>{residenceText(r.country_residence, r.state_residence)}</div>
                <div className="text-xs" style={{ color: colors.muted }}>
                  {residenceBasisLabel(r.residence_basis)}
                </div>
              </td>
              <td className={tdCls}>
                <div>{accreditationLabel(r.accreditation_status)}</div>
                <div className="text-xs" style={{ color: colors.muted }}>
                  {[r.accreditation_category,
                    r.accreditation_expires_at
                      ? `expires ${formatDateTime(r.accreditation_expires_at)}` : null]
                    .filter(Boolean).join(' · ') || '—'}
                </div>
              </td>
              <td className={tdCls}>
                <div>{r.contributions_count}</div>
                <div className="text-xs" style={{ color: colors.muted }}>
                  {formatDateTime(r.first_contribution_at)} to {formatDateTime(r.last_contribution_at)}
                </div>
              </td>
              <td className={`${tdCls} font-mono`}>{moneyMapText(r.gross_contributed)}</td>
              <td className={`${tdCls} font-mono`}>{moneyMapText(r.share_of_vehicle)}</td>
            </tr>
          ))}
        </Table>
      )}
    </div>
  );
}

export function OfferingDefenseView(props: Readonly<{ data: OfferingDefense }>): JSX.Element {
  const { data } = props;
  const docs = data.offering_documents
    .map((d) => `${d.doc_type.replace(/_/g, ' ')} v${d.version} (published ${formatDateTime(d.published_at)})`)
    .join('; ');
  return (
    <div>
      <Facts facts={[
        ['Vehicle', data.vehicle_name],
        ['As of', data.as_of],
        ['Investors against the holder cap', `${data.investors_total} of ${data.holder_cap}`],
        ['Legal limit of holders', String(data.holder_legal_limit)],
        ['Without relationship evidence', String(data.investors_without_relationship_evidence)],
        ['Without acknowledgement of the documents', String(data.investors_without_acknowledgement)],
        ['Offering documents in force', docs || 'None published up to the as-of day'],
      ]} />
      {data.items.length === 0 ? (
        <EmptyRows text="No investor has an approved deposit in this vehicle up to the as-of day." />
      ) : (
        <Table caption="Private-offering defense"
               heads={['Investor', 'Residence', 'Relationship with the firm', 'First offer',
                       'First sale', 'Relationship before sale', 'Documents acknowledged',
                       'Accreditation']}>
          {data.items.map((r: DefenseRow) => (
            <tr key={r.user_id} style={rowStyle}>
              <td className={tdCls}>
                <div className="font-semibold">{r.full_name}</div>
                <div className="text-xs" style={{ color: colors.muted }}>{r.email}</div>
              </td>
              <td className={tdCls}>{residenceText(r.country_residence, null)}</td>
              <td className={tdCls}>
                <div>{relationshipEvidenceLabel(r.relationship_evidence)}</div>
                <div className="text-xs" style={{ color: colors.muted }}>
                  {formatDateTime(r.relationship_established_at)}
                </div>
              </td>
              <td className={tdCls}>{formatDateTime(r.first_offer_at)}</td>
              <td className={tdCls}>{formatDateTime(r.first_sale_at)}</td>
              <td className={tdCls}>{yesNo(r.relationship_before_sale)}</td>
              <td className={tdCls}>{yesNo(r.offering_documents_acknowledged)}</td>
              <td className={tdCls}>{accreditationLabel(r.accreditation_status)}</td>
            </tr>
          ))}
        </Table>
      )}
    </div>
  );
}

export function FirstSalesView(props: Readonly<{
  data: FirstSales;
  vehicles: ReadonlyArray<{ id: string; name: string }>;
}>): JSX.Element {
  const { data, vehicles } = props;
  return (
    <div>
      <Facts facts={[
        ['Form D due', `${data.form_d_days_after_first_sale} days after the first sale`],
      ]} />
      {data.items.length === 0 ? (
        <EmptyRows text="No Rule 506(b) sale has been made yet, so there is no first sale to report." />
      ) : (
        <Table caption="First sale per issuer"
               heads={['Issuer (vehicles)', 'First contract signed', 'First deposit approved',
                       'First sale', 'Form D due', 'Purchasers', 'Not accredited', 'Amounts']}>
          {data.items.map((r) => (
            <tr key={r.legal_entity_id} style={rowStyle}>
              <td className={tdCls}>
                {r.product_ids.map((id) => vehicleNameFor(id, vehicles)).join(', ')}
              </td>
              <td className={tdCls}>{formatDateTime(r.first_contract_signed_at)}</td>
              <td className={tdCls}>{formatDateTime(r.first_deposit_approved_at)}</td>
              <td className={tdCls}>{formatDateTime(r.first_sale_at)}</td>
              <td className={tdCls}>{r.form_d_due_on ?? '—'}</td>
              <td className={tdCls}>{r.purchasers_total}</td>
              <td className={tdCls}>{r.purchasers_not_accredited}</td>
              <td className={`${tdCls} font-mono`}>{moneyMapText(r.amounts_by_currency)}</td>
            </tr>
          ))}
        </Table>
      )}
    </div>
  );
}

export function StateNoticesView(props: Readonly<{
  data: StateNotices;
  vehicles: ReadonlyArray<{ id: string; name: string }>;
}>): JSX.Element {
  const { data, vehicles } = props;
  return (
    <div>
      <Facts facts={[
        ['Notice due', `${data.state_notice_days_after_first_sale} days after the first sale in a state`],
        ['Notice needed before the offer', data.pre_offer_notice_states.join(', ') || 'None'],
        ['Unknown-state bucket', data.unknown_state_code],
      ]} />
      {data.items.length === 0 ? (
        <EmptyRows text="No US resident has an approved deposit yet, so there is no state notice to report." />
      ) : (
        <Table caption="First investor per vehicle and US state"
               heads={['Vehicle', 'State', 'First sale', 'Notice due', 'Before the offer',
                       'Investors', 'Residence known', 'Amounts']}>
          {data.items.map((r) => (
            <tr key={`${r.product_id}:${r.state_code}`} style={rowStyle}>
              <td className={tdCls}>{vehicleNameFor(r.product_id, vehicles)}</td>
              <td className={tdCls}>{stateLabel(r.state_code, data.unknown_state_code)}</td>
              <td className={tdCls}>{formatDateTime(r.first_sale_at)}</td>
              <td className={tdCls}>{r.notice_due_on}</td>
              <td className={tdCls}>{yesNo(r.pre_offer_notice_required)}</td>
              <td className={tdCls}>{r.investors}</td>
              <td className={tdCls}>{residenceBasisLabel(r.residence_basis)}</td>
              <td className={`${tdCls} font-mono`}>{moneyMapText(r.amounts_by_currency)}</td>
            </tr>
          ))}
        </Table>
      )}
    </div>
  );
}

// ── Vehicle choice ──────────────────────────────────────────────────────────

type VehicleList =
  | { status: 'loading' }
  | { status: 'error'; message: string }
  | { status: 'ready'; items: ProductSummary[] };

export function VehicleChooser(props: Readonly<{
  list: VehicleList;
  vehicleId: string;
  asOf: string;
  onVehicle: (id: string) => void;
  onAsOf: (day: string) => void;
}>): JSX.Element {
  const { list } = props;
  return (
    <section className="rounded-2xl p-5 mb-6"
             style={{ background: colors.surface, border: `1px solid ${colors.border}` }}>
      <h2 className="text-base font-bold mb-1" style={{ color: colors.ink }}>Vehicle reports</h2>
      <p className="text-sm mb-4" style={{ color: colors.inkSoft }}>
        The investor register and the private-offering defense report cover one vehicle:
        every investor with an approved deposit in it up to the as-of day.
      </p>
      {list.status === 'loading' && (
        <output className="block text-sm" style={{ color: colors.muted }}>Loading vehicles…</output>
      )}
      {list.status === 'error' && (
        <p role="alert" className="text-sm" style={{ color: colors.danger }}>
          The vehicles could not be read: {list.message}
        </p>
      )}
      {list.status === 'ready' && list.items.length === 0 && (
        <p className="text-sm" style={{ color: colors.muted }}>
          There are no offered vehicles to report on.
        </p>
      )}
      {list.status === 'ready' && list.items.length > 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label htmlFor="reg-vehicle" className={labelCls} style={{ color: colors.inkSoft }}>
              Vehicle
            </label>
            <select id="reg-vehicle" value={props.vehicleId}
                    onChange={(e) => props.onVehicle(e.target.value)}
                    className="w-full px-3 py-2 rounded-lg text-sm" style={field}>
              <option value="">Choose a vehicle…</option>
              {list.items.map((v) => (
                <option key={v.id} value={v.id}>{v.name}</option>
              ))}
            </select>
          </div>
          <div>
            <label htmlFor="reg-as-of" className={labelCls} style={{ color: colors.inkSoft }}>
              As-of day (optional, UTC, included)
            </label>
            <input id="reg-as-of" type="date" value={props.asOf}
                   onChange={(e) => props.onAsOf(e.target.value)}
                   className="w-full px-3 py-2 rounded-lg text-sm" style={field} />
          </div>
        </div>
      )}
    </section>
  );
}

// ── Page ────────────────────────────────────────────────────────────────────

export default function RegulatoryReports(): JSX.Element {
  const [list, setList] = useState<VehicleList>({ status: 'loading' });
  const [vehicleId, setVehicleId] = useState('');
  const [asOf, setAsOf] = useState('');

  const register = useReport<InvestorRegister>();
  const defense = useReport<OfferingDefense>();
  const firstSales = useReport<FirstSales>();
  const stateNotices = useReport<StateNotices>();

  useEffect(() => {
    let live = true;
    portfolioService
      .listAllProducts()
      .then((items) => { if (live) setList({ status: 'ready', items }); })
      .catch((e: unknown) => {
        if (live) setList({ status: 'error', message: getApiErrorMessage(e, 'Try again shortly.') });
      });
    return () => { live = false; };
  }, []);

  const vehicles = list.status === 'ready' ? list.items : [];
  const registerBlocked = vehicleReportBlockedReason({ vehicleId, asOf, busy: register.busy });
  const defenseBlocked = vehicleReportBlockedReason({ vehicleId, asOf, busy: defense.busy });

  return (
    <div className="max-w-6xl">
      <PageHeader
        title="Regulatory reports"
        subtitle="Read-only reports for counsel and auditors. The platform files nothing."
      />
      <p className="text-sm mb-6" style={{ color: colors.inkSoft }}>
        These reports contain the names, e-mail addresses and residence of investors. Every time you
        show or download one, the audit log records it under your name, with the filters you chose.
      </p>

      <VehicleChooser list={list} vehicleId={vehicleId} asOf={asOf}
                      onVehicle={setVehicleId} onAsOf={setAsOf} />

      <ReportCard title="Investor register"
                  description="One row per investor in the vehicle: residence as of the first deposit, accreditation, contribution dates, gross amounts (before any reversal) and share of the vehicle.">
        <ReportActions blocked={registerBlocked} busy={register.busy}
                       showLabel="Show investor register"
                       onShow={() => { void register.show(
                         () => reportsService.investorRegister(vehicleId, asOf),
                         'Could not read the investor register.') }}
                       onDownload={() => { void register.download(
                         () => reportsService.investorRegisterFile(vehicleId, asOf),
                         'Could not download the investor register.') }} />
        <ReportStatus state={register.state} notice={register.notice}>
          {(data) => <InvestorRegisterView data={data} />}
        </ReportStatus>
      </ReportCard>

      <ReportCard title="Private-offering defense"
                  description="Per investor: when the firm first had a relationship with them, whether that came before the sale, whether they acknowledged the offering documents, and the investor count against the holder cap.">
        <ReportActions blocked={defenseBlocked} busy={defense.busy}
                       showLabel="Show offering defense"
                       onShow={() => { void defense.show(
                         () => reportsService.offeringDefense(vehicleId, asOf),
                         'Could not read the offering defense report.') }}
                       onDownload={() => { void defense.download(
                         () => reportsService.offeringDefenseFile(vehicleId, asOf),
                         'Could not download the offering defense report.') }} />
        <ReportStatus state={defense.state} notice={defense.notice}>
          {(data) => <OfferingDefenseView data={data} />}
        </ReportStatus>
      </ReportCard>

      <ReportCard title="Form D clock: first Rule 506(b) sale per issuer"
                  description="For every issuer that has made a Rule 506(b) sale (a US series, or a Mexican SAPI sold to residents of the United States), the first sale and the date Form D falls due. It covers all issuers, so it needs no vehicle choice.">
        <ReportActions blocked={filingReportBlockedReason(firstSales.busy)} busy={firstSales.busy}
                       showLabel="Show first sales"
                       onShow={() => { void firstSales.show(
                         () => reportsService.firstSales(),
                         'Could not read the first-sales report.') }}
                       onDownload={() => { void firstSales.download(
                         () => reportsService.firstSalesFile(),
                         'Could not download the first-sales report.') }} />
        <ReportStatus state={firstSales.state} notice={firstSales.notice}>
          {(data) => <FirstSalesView data={data} vehicles={vehicles} />}
        </ReportStatus>
      </ReportCard>

      <ReportCard title="Blue-sky clock: first investor per state"
                  description="For every vehicle and US state of residence, the first approved sale and the date the state notice falls due. Investors with no recorded state are listed under the unknown-state bucket, never dropped.">
        <ReportActions blocked={filingReportBlockedReason(stateNotices.busy)} busy={stateNotices.busy}
                       showLabel="Show state notices"
                       onShow={() => { void stateNotices.show(
                         () => reportsService.stateNotices(),
                         'Could not read the state-notices report.') }}
                       onDownload={() => { void stateNotices.download(
                         () => reportsService.stateNoticesFile(),
                         'Could not download the state-notices report.') }} />
        <ReportStatus state={stateNotices.state} notice={stateNotices.notice}>
          {(data) => <StateNoticesView data={data} vehicles={vehicles} />}
        </ReportStatus>
      </ReportCard>
    </div>
  );
}
